import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { Search, MapPin, Home, DollarSign, Calendar, ArrowRight, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ApartmentSearchPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    lead_type: "renter",
    budget_min: "",
    budget_max: "",
    bedrooms: "",
    move_in_date: "",
    preferred_areas: [],
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const areas = ["Houston", "Katy", "Sugar Land", "The Woodlands", "Conroe"];
  const bedroomOptions = ["Studio", "1 Bed", "2 Bed", "3+ Bed"];

  const handleAreaToggle = (area) => {
    setFormData(prev => ({
      ...prev,
      preferred_areas: prev.preferred_areas.includes(area)
        ? prev.preferred_areas.filter(a => a !== area)
        : [...prev.preferred_areas, area]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        budget_min: formData.budget_min ? parseInt(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseInt(formData.budget_max) : null,
        source: "apartment_search_page"
      });
      setSubmitted(true);
      toast({
        title: "Request Submitted!",
        description: "We'll send you personalized apartment options soon.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-12 text-center max-w-md shadow-xl"
          data-testid="search-success"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
            Request Received!
          </h2>
          <p className="text-slate-600 mb-6">
            We're already working on finding the perfect apartments for you. Expect a personalized list in your inbox within 24 hours.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="btn-primary"
            data-testid="search-new-request"
          >
            Submit Another Request
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-[#0A2342] py-16 md:py-24">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center px-4 py-2 bg-[#DE6E4B] rounded-full text-white text-sm font-medium mb-6">
              <Search className="w-4 h-4 mr-2" />
              Free Apartment Search Service
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']">
              Find Your Perfect Houston Apartment
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Tell us what you're looking for and we'll send you a personalized list of apartments that match your needs. It's completely free!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 md:p-12 shadow-xl"
              data-testid="apartment-search-form"
            >
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Info */}
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2342] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-[#DE6E4B] rounded-full flex items-center justify-center text-white text-sm mr-3">1</span>
                    Your Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input-field"
                        placeholder="John Smith"
                        data-testid="search-form-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-field"
                        placeholder="john@email.com"
                        data-testid="search-form-email"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Phone (Optional)</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-field"
                        placeholder="(555) 123-4567"
                        data-testid="search-form-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Lead Type */}
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2342] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-[#DE6E4B] rounded-full flex items-center justify-center text-white text-sm mr-3">2</span>
                    I'm Looking To
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: "renter", label: "Rent", icon: Home },
                      { value: "buyer", label: "Buy", icon: DollarSign },
                      { value: "investor", label: "Invest", icon: MapPin }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, lead_type: option.value })}
                        data-testid={`search-form-type-${option.value}`}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          formData.lead_type === option.value
                            ? "border-[#DE6E4B] bg-[#DE6E4B]/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <option.icon className={`w-6 h-6 mx-auto mb-2 ${
                          formData.lead_type === option.value ? "text-[#DE6E4B]" : "text-slate-400"
                        }`} />
                        <span className={`font-medium ${
                          formData.lead_type === option.value ? "text-[#0A2342]" : "text-slate-600"
                        }`}>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Budget */}
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2342] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-[#DE6E4B] rounded-full flex items-center justify-center text-white text-sm mr-3">3</span>
                    Budget & Preferences
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Min Budget</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="number"
                          value={formData.budget_min}
                          onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                          className="input-field pl-10"
                          placeholder="1000"
                          data-testid="search-form-budget-min"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Max Budget</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="number"
                          value={formData.budget_max}
                          onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                          className="input-field pl-10"
                          placeholder="2500"
                          data-testid="search-form-budget-max"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Bedrooms</label>
                    <div className="flex flex-wrap gap-2">
                      {bedroomOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setFormData({ ...formData, bedrooms: option })}
                          data-testid={`search-form-bed-${option.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`py-2 px-4 rounded-full text-sm font-medium transition-all ${
                            formData.bedrooms === option
                              ? "bg-[#0A2342] text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Move-in Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="date"
                        value={formData.move_in_date}
                        onChange={(e) => setFormData({ ...formData, move_in_date: e.target.value })}
                        className="input-field pl-10"
                        data-testid="search-form-move-date"
                      />
                    </div>
                  </div>
                </div>

                {/* Areas */}
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2342] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-[#DE6E4B] rounded-full flex items-center justify-center text-white text-sm mr-3">4</span>
                    Preferred Areas
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {areas.map((area) => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => handleAreaToggle(area)}
                        data-testid={`search-form-area-${area.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`py-2 px-4 rounded-full text-sm font-medium transition-all flex items-center ${
                          formData.preferred_areas.includes(area)
                            ? "bg-[#DE6E4B] text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        {area}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <h3 className="text-xl font-semibold text-[#0A2342] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-[#DE6E4B] rounded-full flex items-center justify-center text-white text-sm mr-3">5</span>
                    Anything Else?
                  </h3>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="textarea-field"
                    rows={4}
                    placeholder="Tell us about any specific requirements, preferred amenities, or questions you have..."
                    data-testid="search-form-message"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full text-lg py-4 flex items-center justify-center"
                  data-testid="search-form-submit"
                >
                  {loading ? (
                    <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                  ) : (
                    <>Get My Personalized List <ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApartmentSearchPage;
